<script lang='ts'>
import type { PropType, SetupContext } from 'vue'
import type { PaginationModel } from '../types/page'
import { computed, defineComponent, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { getElementTop } from '../utils'
import MsPagination from './ms-pagination.vue'

export default defineComponent({
  name: 'MsTable',
  components: { MsPagination },
  props: {
    showPagination: {
      type: Boolean,
      default: true,
    },
    needHeight: {
      type: Boolean,
      default: true,
    },
    fitContent: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Array,
      required: true,
    },
    pagination: {
      type: Object as PropType<PaginationModel>,
    },
    bottomHeight: {
      type: Number,
      default: -1,
    },
  },
  emits: ['selection-change', 'change-page', 'row-click'],
  setup(props, ctx: SetupContext) {
    const tableHeight = ref<string | number | undefined>(300)
    const tableRef = ref()
    const tableBottom = computed(() => {
      return props.bottomHeight !== -1 ? props.bottomHeight : props.showPagination ? 62 : 20
    })
    if (props.fitContent) {
      tableHeight.value = undefined
    }
    const setTableHeight = () => {
      tableHeight.value = window.innerHeight - getElementTop(tableRef.value.$el) - tableBottom.value
    }
    onMounted(() => {
      if (props.needHeight) {
        nextTick(() => {
          setTableHeight()
          window.addEventListener('resize', setTableHeight, false)
        })
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', setTableHeight)
    })

    function handleSelectionChange(val: any) {
      ctx.emit('selection-change', val)
    }

    function changePage(page: number) {
      ctx.emit('change-page', page)
    }

    function handleRowClick(row: any) {
      ctx.emit('row-click', row)
    }

    function toggleSelection(row: any, selected: boolean) {
      tableRef.value.toggleRowSelection(row, selected)
    }

    function sort(prop: string, order: string) {
      tableRef.value.sort(prop, order)
    }

    function clearSort() {
      tableRef.value.clearSort()
    }

    return {
      tableHeight,
      tableRef,
      handleSelectionChange,
      changePage,
      handleRowClick,
      toggleSelection,
      sort,
      clearSort,
      resetHeight: setTableHeight,
    }
  },
})
</script>

<template>
  <div>
    <el-table
      ref="tableRef" :data="data" :height="tableHeight" v-bind="$attrs"
      @selection-change="handleSelectionChange"
      @row-click="handleRowClick"
    >
      <slot />
    </el-table>
    <MsPagination v-if="showPagination" :pagination="pagination" @change-page="changePage" />
  </div>
</template>

<style scoped>

</style>
